import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  CheckCircle,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getStabilityMeta } from '@/lib/assessmentUtils';
import { UI, STABILITY_LEVELS, BRAND } from '@/lib/constants';
import { DomainRadarChart, ExtendedBarChart } from '@/components/ResultCharts';
import apiClient from '@/lib/apiClient';
import PlainLanguageInterpretation from '@/components/results/PlainLanguageInterpretation';
import PriorityObservations from '@/components/results/PriorityObservations';
import ImmediateActions from '@/components/results/ImmediateActions';
import ProfessionalReportCTA from '@/components/results/ProfessionalReportCTA';
import ProfessionalInsights from '@/components/results/ProfessionalInsights';
import { PaidUnlockedCard } from '@/components/results/UnlockProReport';
import usePaymentPolling from '@/hooks/usePaymentPolling';
import {
  getPlainLanguageInterpretation,
  getPriorityObservations,
} from '@/lib/resultsInsights';
import { formatMytLong } from '@/lib/datetime';

const NAVY = '#0B2951';
const TEAL = '#008C85';
const GRAY = '#6D7586';

const STABILITY_ICON_PROPS = { size: UI.STABILITY_ICON_SIZE, weight: 'fill', className: 'text-white' };
const STABILITY_ICON_MAP = {
  [STABILITY_LEVELS.HIGH]: CheckCircle,
  [STABILITY_LEVELS.MODERATE]: Warning,
  [STABILITY_LEVELS.LOW]: XCircle,
};

const pickStabilityIcon = (level) => {
  const Icon = STABILITY_ICON_MAP[level] || XCircle;
  return <Icon {...STABILITY_ICON_PROPS} />;
};

const buildRadarData = (a) => Object.entries(a.domain_scores).map(([d, s]) => ({ domain: d, score: s }));
const buildBarData = (a) => Object.entries(a.extended_scores).map(([d, s]) => ({ domain: d, score: s }));

// Maps a numeric domain score (0–5) to an accent colour. Extracted from a
// nested ternary for readability; thresholds match the brand palette used
// throughout the diagnostic results UI.
const pickScoreAccent = (score) => {
  if (score >= 4) return TEAL;
  if (score >= 3) return '#D97706';
  return '#B91C1C';
};

// Module-level fetch helper. Lives outside any hook scope so the `useCallback`
// below has zero local variables for static analysers to misread. All side
// effects (state setters) are passed in as parameters — never closed over.
const loadAssessmentById = async (assessmentId, onData, onLoading) => {
  onLoading(true);
  try {
    const response = await apiClient.get(`/assessments/${assessmentId}`);
    onData(response.data);
  } catch {
    toast.error('Failed to load assessment results');
  } finally {
    onLoading(false);
  }
};

// ===== Sub-components =====

const LoadingState = () => (
  <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600">Loading assessment results...</p>
    </div>
  </div>
);

const NotFoundState = ({ onBack }) => (
  <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
    <div className="text-center">
      <XCircle size={64} weight="duotone" className="text-red-500 mx-auto mb-4" />
      <h2 className="text-2xl font-semibold text-slate-900 mb-2">Assessment Not Found</h2>
      <Button onClick={onBack}>Return to Home</Button>
    </div>
  </div>
);

const formatAssessmentDate = (assessment) => {
  const raw = assessment.created_at || assessment.completed_at || assessment.submitted_at;
  if (!raw) return null;
  const formatted = formatMytLong(raw);
  return formatted === '-' ? null : formatted;
};

const FreeResultsHeader = ({ assessment, onBack }) => {
  const date = formatAssessmentDate(assessment);
  return (
    <header className="mb-8" data-testid="free-results-header">
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-5 -ml-2"
        data-testid="back-to-home-button"
      >
        <ArrowLeft size={20} className="mr-2" />Back to Home
      </Button>
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 tracking-wide"
        style={{ background: `${TEAL}1A`, color: TEAL }}
        data-testid="assessment-type-badge"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: TEAL }} />
        Free Diagnostic Snapshot
      </div>
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black mb-2 leading-[1.1]"
        style={{ color: NAVY }}
        data-testid="results-page-title"
      >
        AWSAI Free Assessment Result
      </h1>
      <p
        className="text-base sm:text-lg font-medium mb-5"
        style={{ color: TEAL }}
        data-testid="results-page-subtitle"
      >
        Workflow Stability &amp; AI Readiness Snapshot
      </p>
      <dl
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-3xl text-sm"
        data-testid="respondent-meta"
      >
        <div className="flex gap-2">
          <dt className="font-semibold" style={{ color: NAVY }}>Organisation:</dt>
          <dd style={{ color: GRAY }}>{assessment.respondent?.organization_name || '—'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold" style={{ color: NAVY }}>Respondent:</dt>
          <dd style={{ color: GRAY }}>{assessment.respondent?.name || '—'}</dd>
        </div>
        {date && (
          <div className="flex gap-2">
            <dt className="font-semibold" style={{ color: NAVY }}>Completed (MYT):</dt>
            <dd style={{ color: GRAY }} data-testid="assessment-completed-date">{date}</dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="font-semibold" style={{ color: NAVY }}>Assessment type:</dt>
          <dd style={{ color: GRAY }}>Free Diagnostic Snapshot</dd>
        </div>
      </dl>
    </header>
  );
};

const FOCUS_BY_LEVEL = {
  [STABILITY_LEVELS.HIGH]:
    'Workflows are sufficiently stable to support selective AI adoption. Focus on validated pilots and measurement.',
  [STABILITY_LEVELS.MODERATE]:
    'Some workflows may be suitable for selective automation. Unstable areas should be standardised before broader AI adoption.',
  [STABILITY_LEVELS.LOW]:
    'Workflow standardisation is the priority. Defer broader AI adoption until foundational stability is achieved.',
};

const OverallScoreCard = ({ assessment, stabilityMeta, stabilityIcon }) => {
  const focus = FOCUS_BY_LEVEL[assessment.stability_level] || FOCUS_BY_LEVEL[STABILITY_LEVELS.LOW];
  return (
    <section
      className="rounded-2xl p-6 sm:p-8 mb-8 shadow-lg text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #103A6D 50%, ${TEAL} 100%)` }}
      data-testid="overall-score-card"
    >
      <div className="flex flex-col lg:flex-row items-stretch gap-6">
        <div className="flex items-center gap-5 flex-1">
          <div className="flex-shrink-0">{stabilityIcon}</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] opacity-80 font-bold mb-1">
              Overall AWSAI Score
            </div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl sm:text-6xl font-black" data-testid="overall-score-value">
                {Number(assessment.overall_score).toFixed(2)}
              </span>
              <span className="text-lg opacity-80">/ 5.00</span>
            </div>
            <div className="text-lg sm:text-xl font-semibold" data-testid="stability-category">
              {assessment.stability_level}
            </div>
          </div>
        </div>
        <div className="lg:w-px lg:bg-white/20 lg:my-2 hidden lg:block" aria-hidden="true" />
        <div className="flex-1 max-w-xl">
          <div className="text-xs uppercase tracking-[0.25em] opacity-80 font-bold mb-1">
            Recommended Focus
          </div>
          <p className="text-sm sm:text-base text-slate-100/95 leading-relaxed mb-3">
            {focus}
          </p>
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide opacity-90">
            SMART Phase · <span>{stabilityMeta.smartPhase}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const DomainSnapshot = ({ coreScores, extendedScores }) => {
  const all = [
    ...Object.entries(coreScores).map(([d, s]) => ({ domain: d, score: s, group: 'AWSAI Core' })),
    ...Object.entries(extendedScores).map(([d, s]) => ({ domain: d, score: s, group: 'Extended Module' })),
  ];
  return (
    <section className="mb-8" data-testid="domain-snapshot-section">
      <h3 className="text-lg sm:text-xl font-bold mb-1" style={{ color: NAVY }}>
        Domain Snapshot
      </h3>
      <p className="text-sm mb-5" style={{ color: GRAY }}>
        AWSAI Core and Extended Module scores at a glance.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {all.map(({ domain, score, group }) => {
          const v = Number(score);
          const accent = pickScoreAccent(v);
          return (
            <div
              key={domain}
              className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`domain-score-card-${domain.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div
                className="text-[10px] tracking-[0.18em] uppercase font-bold mb-2"
                style={{ color: GRAY }}
              >
                {group}
              </div>
              <div className="text-sm font-semibold mb-2 leading-tight" style={{ color: NAVY }}>
                {domain}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-black" style={{ color: accent }}>
                  {v.toFixed(1)}
                </span>
                <span className="text-xs" style={{ color: GRAY }}>/ 5.0</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const IpFooter = () => (
  <div className="mt-12 pt-6 border-t border-slate-200 text-center">
    <p className="text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed">{BRAND.IP_NOTICE}</p>
  </div>
);

const FreeResults = ({ assessment, assessmentId }) => {
  const stabilityMeta = getStabilityMeta(assessment.stability_level);
  const stabilityIcon = pickStabilityIcon(assessment.stability_level);
  const interpretation = getPlainLanguageInterpretation(assessment.stability_level);
  const observations = getPriorityObservations(assessment);

  return (
    <>
      <OverallScoreCard
        assessment={assessment}
        stabilityMeta={stabilityMeta}
        stabilityIcon={stabilityIcon}
      />
      <PlainLanguageInterpretation interpretation={interpretation} stabilityMeta={stabilityMeta} />
      <DomainSnapshot
        coreScores={assessment.domain_scores}
        extendedScores={assessment.extended_scores}
      />
      <PriorityObservations observations={observations} />
      <ImmediateActions />
      <ProfessionalReportCTA assessment={assessment} assessmentId={assessmentId} />
    </>
  );
};

const PaidFullReport = ({ assessment, assessmentId }) => (
  <>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <DomainRadarChart data={buildRadarData(assessment)} />
      <ExtendedBarChart data={buildBarData(assessment)} />
    </div>
    <ProfessionalInsights
      assessmentId={assessmentId}
      overallScore={assessment.overall_score}
    />
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm mb-8">
      <h3 className="text-lg font-semibold text-slate-900 mb-2">Want the full deliverable?</h3>
      <p className="text-sm text-slate-600">
        The Professional PDF report contains the full domain analysis, benchmark comparison,
        SMART-Workflow roadmap, 30-day quick wins, 90-day roadmap, 6-month transformation direction,
        priority heat map, urgency narrative, and consultant recommendations.
        Use the download button above to save your copy.
      </p>
    </div>
  </>
);

const ResultsContent = ({ assessment, assessmentId, navigate, refresh }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isPaid = assessment.payment_status === 'paid';

  // After Stripe redirect (if ever re-enabled), poll for confirmation then drop the query param.
  const onPaid = useCallback(() => {
    refresh();
    setSearchParams({});
  }, [refresh, setSearchParams]);
  usePaymentPolling({ sessionId: !isPaid && sessionId ? sessionId : null, onPaid });

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 sm:py-12">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <FreeResultsHeader assessment={assessment} onBack={() => navigate('/')} />
        {isPaid ? (
          <>
            <PaidUnlockedCard assessmentId={assessmentId} assessment={assessment} />
            <PaidFullReport assessment={assessment} assessmentId={assessmentId} />
          </>
        ) : (
          <FreeResults assessment={assessment} assessmentId={assessmentId} />
        )}
        <IpFooter />
      </div>
    </div>
  );
};

const ResultsDashboard = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);

  // Single source of truth for loading the assessment.
  // Body delegates to the module-level helper `loadAssessmentById` so this
  // useCallback closes over only one reactive value (`assessmentId`). The
  // setState setters (`setAssessment`, `setLoading`) are stable per the
  // React docs and are NOT dependencies. `loadAssessmentById` is a module
  // import (stable). The dep array `[assessmentId]` is therefore exhaustive.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAssessment = useCallback(
    () => loadAssessmentById(assessmentId, setAssessment, setLoading),
    [assessmentId],
  );

  // Stable refresh handler passed down to children (e.g. payment polling).
  const refresh = useCallback(() => { fetchAssessment(); }, [fetchAssessment]);

  // Initial / assessmentId-change load.
  useEffect(() => { fetchAssessment(); }, [fetchAssessment]);

  if (loading) return <LoadingState />;
  if (!assessment) return <NotFoundState onBack={() => navigate('/')} />;
  return (
    <ResultsContent
      assessment={assessment}
      assessmentId={assessmentId}
      navigate={navigate}
      refresh={refresh}
    />
  );
};

export default ResultsDashboard;
